import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UI = {
  sk: {
    title: "SauberEko® AI poradca",
    subtitle: "Pomôžem s otázkami o certifikácii",
    placeholder: "Napíšte svoju otázku…",
    greeting: "Dobrý deň, som AI poradca SauberEko®. Rád vám poradím s certifikáciou, cenami či postupom. Čo vás zaujíma?",
    suggestions: ["Ako prebieha certifikácia?", "Koľko stojí certifikát?", "Ako začať?"],
    error: "Prepáčte, nastala chyba. Skúste to znova.",
  },
  en: {
    title: "SauberEko® AI Advisor",
    subtitle: "Here to help with certification",
    placeholder: "Type your question…",
    greeting: "Hello, I'm the SauberEko® AI advisor. I'm happy to help with certification, pricing or the process. What would you like to know?",
    suggestions: ["How does certification work?", "How much does it cost?", "How do I start?"],
    error: "Sorry, something went wrong. Please try again.",
  },
  de: {
    title: "SauberEko® KI-Berater",
    subtitle: "Hilfe rund um die Zertifizierung",
    placeholder: "Stellen Sie Ihre Frage…",
    greeting: "Guten Tag, ich bin der SauberEko® KI-Berater. Gerne helfe ich bei Zertifizierung, Preisen oder Ablauf. Was möchten Sie wissen?",
    suggestions: ["Wie läuft die Zertifizierung ab?", "Was kostet die Zertifizierung?", "Wie fange ich an?"],
    error: "Entschuldigung, ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.",
  },
};

function renderText(text) {
  const lines = text.split("\n");
  return lines.map((line, i) => {
    const bulletMatch = line.match(/^\s*[-•]\s+(.*)$/);
    const content = bulletMatch ? bulletMatch[1] : line;
    const parts = content.split(/(\*\*[^*]+\*\*)/g).map((p, j) =>
      p.startsWith("**") && p.endsWith("**") ? (
        <strong key={j} className="text-[#E8D9A8] font-semibold">{p.slice(2, -2)}</strong>
      ) : (
        <span key={j}>{p}</span>
      )
    );
    if (bulletMatch) {
      return (
        <div key={i} className="flex gap-2 pl-1">
          <span className="text-[#D4AF37] mt-0.5">•</span>
          <span>{parts}</span>
        </div>
      );
    }
    if (line.trim() === "") return <div key={i} className="h-2" />;
    return <p key={i}>{parts}</p>;
  });
}

export const AssistantWidget = () => {
  const { lang } = useLang();
  const ui = UI[lang] || UI.sk;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  if (!sessionRef.current) {
    const key = "saubereko_assistant_session";
    let s = typeof window !== "undefined" ? localStorage.getItem(key) : null;
    if (!s) {
      s = (crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`).replace(/-/g, "");
      if (typeof window !== "undefined") localStorage.setItem(key, s);
    }
    sessionRef.current = s;
  }

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  }, []);

  useEffect(() => { if (open) scrollToBottom(); }, [messages, open, scrollToBottom]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: q, language: lang }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const evt of events) {
          const line = evt.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + payload.delta };
              return copy;
            });
            scrollToBottom();
          } else if (payload.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", text: payload.error };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        if (copy.length && copy[copy.length - 1].role === "assistant" && !copy[copy.length - 1].text) {
          copy[copy.length - 1] = { role: "assistant", text: ui.error };
        }
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="assistant-toggle"
        onClick={() => setOpen((o) => !o)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1, type: "spring", stiffness: 200, damping: 18 }}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.95 }}
        aria-label={ui.title}
        className="fixed bottom-6 right-6 z-[60] h-14 w-14 rounded-full bg-[#D4AF37] text-black shadow-[0_0_30px_rgba(212,175,55,0.45)] flex items-center justify-center hover:bg-[#F2E3C6] transition-colors"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="assistant-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="fixed bottom-24 right-4 sm:right-6 z-[60] w-[calc(100vw-2rem)] sm:w-[400px] h-[560px] max-h-[calc(100vh-7rem)] flex flex-col border border-[#D4AF37]/30 bg-[#0a0a0a] shadow-2xl overflow-hidden"
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 bg-gradient-to-r from-[#1a1407] to-[#0a0a0a]">
              <div className="h-9 w-9 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center shrink-0">
                <Sparkles size={17} className="text-[#D4AF37]" />
              </div>
              <div className="min-w-0">
                <p className="font-display text-[#F2E3C6] leading-tight truncate">{ui.title}</p>
                <p className="text-white/40 text-xs truncate">{ui.subtitle}</p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4 text-sm">
              <div className="flex justify-start">
                <div className="max-w-[88%] bg-white/[0.04] border border-white/10 rounded-2xl rounded-tl-sm px-4 py-3 text-white/85 leading-relaxed space-y-1">
                  {renderText(ui.greeting)}
                </div>
              </div>

              {messages.length === 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {ui.suggestions.map((s, i) => (
                    <button
                      key={i}
                      data-testid={`assistant-suggestion-${i}`}
                      onClick={() => send(s)}
                      className="text-xs border border-[#D4AF37]/40 text-[#D4AF37] px-3 py-1.5 rounded-full hover:bg-[#D4AF37]/10 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}

              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={m.role === "user" ? "assistant-user-msg" : "assistant-bot-msg"}
                    className={`max-w-[88%] rounded-2xl px-4 py-3 leading-relaxed space-y-1 ${
                      m.role === "user"
                        ? "bg-[#D4AF37] text-black rounded-tr-sm font-medium"
                        : "bg-white/[0.04] border border-white/10 text-white/85 rounded-tl-sm"
                    }`}
                  >
                    {m.text ? renderText(m.text) : (
                      <span className="inline-flex gap-1 py-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37] animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37] animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37] animate-bounce" style={{ animationDelay: "300ms" }} />
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-white/10 p-3 flex items-end gap-2">
              <textarea
                data-testid="assistant-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                rows={1}
                placeholder={ui.placeholder}
                className="flex-1 resize-none bg-[#050505] border border-white/15 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#D4AF37] max-h-28"
              />
              <button
                data-testid="assistant-send"
                onClick={() => send()}
                disabled={streaming || !input.trim()}
                className="h-10 w-10 rounded-lg bg-[#D4AF37] text-black flex items-center justify-center hover:bg-[#F2E3C6] transition-colors disabled:opacity-40 shrink-0"
                aria-label="Send"
              >
                <Send size={17} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
